import clsx from 'clsx';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { BillingModeSwitch } from '@/components/Elements/BillingModeSwitch';
import { CurrencySwitch } from '@/components/Elements/CurrencySwitch';
import { FeatureComparatorLink } from '@/components/Elements/FeatureComparatorLink';
import { ProductCardsContainer } from '@/components/Elements/ProductCardsContainer';
import { Slider, SliderProps } from '@/components/Elements/Slider';
import { BILLING_MODE_MONTH, BILLING_MODE_YEAR } from '@/config/appConstants';
import { useUserProducts } from '@/hooks/useUserProducts';
import {
  BillingMode,
  Currency,
  ParsedSliderMarkType,
  ProductTypes,
  SliderMarkType,
  UserProductTypes,
} from '@/types';
import {
  getBillingModeByIntervalCount,
  getOffset,
  shouldShowTheBillingModeSwitch,
  shouldShowTheCurrencySwitch,
} from '@/utils/helpers';

const ProductCardsContainerWrap = styled.div`
  .productCardsContainerInner {
    padding: 10px 0 30px;
    transition: all 0.5s ease 0s;
  }
  .collection__comparator--mode {
    max-width: 950px;
  }
  .public--page .comparator--mode.clean {
    background-position: 0 260px;
  }

  /* Only for desktop and non mobile devices */
  @media only screen and (min-width: 415px) {
    .productCardsContainerInner {
      top: -260px;
    }
    .is-sticky {
      padding: 10px 56px;
      top: 0;
      z-index: 50;
    }
    .is-sticky .md__product__featured__badge {
      display: none;
    }
    .is-sticky article {
      padding-top: 10px;
    }
    .is-sticky header {
      padding-top: 10px;
      padding-bottom: 10px;
    }
    .is-sticky .card__category__name {
      font-size: 18px;
    }
    .is-sticky button {
      padding-top: 2px;
      padding-bottom: 2px;
      margin: 5px;
      font-size: 16px;
      width: calc(100% - 10px);
    }
    .is-sticky button span {
      margin: 0;
    }
    .is-sticky .card__price {
      margin: 0;
      font-size: 28px;
    }
  }
`;

export type EmailMarketingContainerProps = {
  title: string;
  freemiumProduct: ProductTypes;
  products: ProductTypes[][];
  userProducts?: UserProductTypes[] | undefined;
  onProductSelect: (product: ProductTypes) => void;
  onRequestInfo: (product: ProductTypes) => void;
  onFreeProductSelect: (product: ProductTypes) => void;
  showFeatures?: boolean;
  userIsLogged?: boolean;
  productCardsContainerClass?: string;
  stickyMode?: boolean;
  publicPage?: boolean;
  billingMode: BillingMode;
} & SliderProps;

export const getProducts = (
  products: ProductTypes[][],
  marks: any,
  productStep = 0
): ProductTypes[] =>
  products
    .map(
      (product: ProductTypes[]) =>
        product.filter((item: ProductTypes) => item.info.productStep === marks[productStep])[0]
    )
    .filter((item) => !!item);

export const EmailMarketingContainer = ({
  title,
  min = 0,
  max,
  freemiumProduct,
  products,
  userProducts,
  onSliderChange,
  onProductSelect,
  onFreeProductSelect,
  onRequestInfo,
  defaultValue = 0,
  marks = {},
  showFeatures,
  userIsLogged,
  productCardsContainerClass,
  stickyMode = false,
  publicPage,
  billingMode = BILLING_MODE_MONTH,
}: EmailMarketingContainerProps) => {
  const { t } = useTranslation();
  const [menuBarTop, setMenuBarTop] = useState<number | undefined>(undefined);
  const productsCardContainerInner = useRef<HTMLDivElement>(null);
  const step = 1;
  const { setSelectedProductCurrency, getSelectedUserProduct, setSelectedProductBillingMode } =
    useUserProducts();
  const [productsCollection, setProductsCollection] = useState(
    getProducts(products, marks, defaultValue)
  );
  const [currentStep, setCurrentStep] = useState(defaultValue);
  const [currentBillingMode, setCurrentBillingMode] = useState(billingMode);

  const [productsSticky, setProductsSticky] = useState(false);
  const { selectedProductCurrency, selectedProductIntervalBillingMode, selectedProduct } =
    getSelectedUserProduct();

  const parseMarks = (marks: SliderMarkType) => {
    const keys = Object.keys(marks);
    const result: ParsedSliderMarkType = {};

    keys.forEach((key: string) => {
      const iKey = +key;

      result[iKey] = (
        <div className="min-w-[75px]">
          <span className="hidden lg:block font-bold block text-black">
            {iKey === keys.length - 1 ? '>' : ''}
            {t(`productSteps.${marks[iKey]}`)}
          </span>
          <span className="hidden lg:block block text-black">{t(`common.emails`)}</span>
        </div>
      );
    });

    return result;
  };

  const marksParsed = parseMarks(marks);

  const unselectStep = () => {
    const currentSelectedStepLabel = document.querySelector('.step--selected');
    currentSelectedStepLabel && currentSelectedStepLabel.classList.remove('step--selected');
  };

  const selectStep = (step: number = currentStep + 1) => {
    unselectStep();

    const currentStepLabel = document.querySelector(
      `.rc-slider-mark-text:nth-of-type(${step}) > div`
    );

    currentStepLabel && currentStepLabel.classList.add('step--selected');
  };

  const isSticky = () => {
    const menuBarEle = productsCardContainerInner.current;
    const scrollTop = window.scrollY;

    if (!menuBarEle || typeof menuBarTop === 'undefined') {
      return false;
    }
    const nextElement = document.querySelector('.helpContainer--section');
    const nextElementTop = nextElement ? getOffset(nextElement).top : undefined;

    const conditionA = scrollTop >= menuBarTop + 290;
    const conditionB = nextElementTop ? scrollTop < nextElementTop : true;

    setProductsSticky(conditionA && conditionB);
  };

  useLayoutEffect(() => {
    const menuBarEle = productsCardContainerInner.current;

    if (menuBarEle) {
      const { top } = menuBarEle.getBoundingClientRect();
      setMenuBarTop(top);
    }
  }, []);

  useEffect(() => {
    selectStep();

    if (!stickyMode) {
      return;
    }

    window.addEventListener('scroll', isSticky);

    return () => {
      window.removeEventListener('scroll', isSticky);
    };
  });

  const onDefaultSliderChange = (value: number) => {
    setCurrentStep(value);
    setProductsCollection(getProducts(products, marks, value));
    selectStep(value);
    onSliderChange && onSliderChange(value);
  };

  const onChangeCurrencySwitch = (event: React.ChangeEvent<HTMLInputElement>) =>
    setSelectedProductCurrency(event.target.value as Currency);

  const onChangeBillingModeSwitch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const billingModeValue = event.target.checked ? BILLING_MODE_YEAR : BILLING_MODE_MONTH;
    setSelectedProductBillingMode(billingModeValue);
    setCurrentBillingMode(billingModeValue);
  };

  const onComparatorLinkClick = () => {
    const urlRedirect = publicPage ? '/store/products' : '/account/products';
    window.location.assign(urlRedirect);
  };

  const onDefaultProductSelect = (product: ProductTypes) => {
    setSelectedProductBillingMode(currentBillingMode);
    onProductSelect && onProductSelect(product);
  };

  const onDefaultFreeProductSelect = (product: ProductTypes) => {
    setSelectedProductBillingMode(currentBillingMode);
    onFreeProductSelect && onFreeProductSelect(product);
  };

  return (
    <section
      className={clsx('bg-md-yellow', {
        clean: !productsSticky,
        'comparator--mode': !showFeatures,
      })}
    >
      <div className="max-w-6xl m-auto px-3 py-2 lg:px-8 lg:pt-10 lg:pb-5">
        <h2 className="md__payments__title--h2">{title}</h2>
        <div className="flex justify-center lg:justify-between flex-wrap lg:flex-nowrap">
          <div
            className={clsx('mr-0 lg:mr-20 w-full relative', {
              'lg:w-11/12':
                !shouldShowTheBillingModeSwitch() || !shouldShowTheCurrencySwitch(userProducts),
              'lg:w-7/12':
                shouldShowTheBillingModeSwitch() && shouldShowTheCurrencySwitch(userProducts),
            })}
          >
            <Slider
              min={min}
              max={max}
              step={step}
              onSliderChange={onDefaultSliderChange}
              defaultValue={defaultValue}
              marks={marksParsed}
              selectMarks={marks}
              selectClass="mb-5 lg:mb-0"
            />
          </div>
          <div className="mr-0 mt-4 lg:mt-[-70px] flex justify-center lg:justify-between items-end flex-wrap z-[5]">
            {shouldShowTheBillingModeSwitch() && (
              <BillingModeSwitch
                className={clsx({
                  'mt-6 mr-0': !shouldShowTheCurrencySwitch(userProducts),
                })}
                onChange={onChangeBillingModeSwitch}
                billingModeDefaultValue={billingMode}
              />
            )}
            {shouldShowTheCurrencySwitch(userProducts) && (
              <div className="w-[150px] flex justify-center items-center flex-wrap">
                <CurrencySwitch
                  defaultValue={selectedProductCurrency}
                  onChange={onChangeCurrencySwitch}
                  className="ml-8"
                  title={t('common.currency.title')}
                />
              </div>
            )}
          </div>
        </div>

        <ProductCardsContainerWrap>
          <div className="productCardsContainerWrap relative">
            <div
              ref={productsCardContainerInner}
              className={clsx('productCardsContainerInner', {
                'is-sticky relative lg:fixed left-0 right-0 bg-md-yellow': productsSticky,
              })}
            >
              <ProductCardsContainer
                className={productCardsContainerClass}
                freemiumProduct={freemiumProduct}
                productsCollection={productsCollection}
                userCurrency={selectedProductCurrency}
                userProducts={userProducts}
                billingMode={
                  (selectedProduct &&
                    getBillingModeByIntervalCount(selectedProductIntervalBillingMode)) ||
                  currentBillingMode
                }
                onProductSelect={onDefaultProductSelect}
                onFreeProductSelect={onDefaultFreeProductSelect}
                onRequestInfo={onRequestInfo}
                userIsLogged={userIsLogged}
                showFeatures={showFeatures}
                isSticky={productsSticky}
              />
            </div>
          </div>
        </ProductCardsContainerWrap>
        {!showFeatures && (
          <p className="text-right italic text-xs mt-1">{t('ProductDetail.taxesDisclaimer')}</p>
        )}
        {!showFeatures && (
          <FeatureComparatorLink
            onComparatorLinkClick={onComparatorLinkClick}
            actionLabel={t('FeatureComparator.products')}
          />
        )}
      </div>
    </section>
  );
};
